import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseCookies } from 'nookies'
import debounce from 'lodash.debounce'

import { fetchPlacesAutocomplete } from 'features/places/services/placesAutocompleteApi'
import type {
  PlaceAutocompletePrediction,
  PlacesAutocompleteStatus,
} from 'features/places/types/placesAutocomplete'
import { toAnalyticsLocation } from 'features/tracking/lib/location'
import {
  GTM_EVENT_LOCATION_SEARCH_SELECT,
  GTM_EVENT_SPACE_ADDRESS_SEARCH,
  sendGtmLocationEvent,
} from 'lib/gtag'
import { goolgeMapGetPlaceDetail } from 'state/ducks/locations/apis'

export const usePlaceAutoCompletion = () => {
  const cookies = parseCookies()
  const [searchText, setSearchTextState] = useState<string>('')
  const [places, setPlaces] = useState<PlaceAutocompletePrediction[]>([])
  const [status, setStatus] = useState<PlacesAutocompleteStatus | null>(null)
  // 入力欄への初期値の反映で実行される検索は計測しない
  const isUserInputRef = useRef(false)

  const setSearchText = useCallback((text: string) => {
    isUserInputRef.current = false
    setSearchTextState(text)
  }, [])

  const setSearchTextFromInput = useCallback((text: string) => {
    isUserInputRef.current = true
    setSearchTextState(text)
  }, [])

  const searchPlaces = useCallback(
    async (text: string) => {
      if (!text) return

      try {
        const result = await fetchPlacesAutocomplete(text, cookies.jwt_token)
        setStatus(result.status)

        if (result.status === 'OK') {
          setPlaces(result.predictions)
        } else {
          setPlaces([])
        }
      } catch {
        setPlaces([])
        setStatus(null)
      }
    },
    [cookies.jwt_token]
  )

  const debouncedSearch = useMemo(() => {
    return debounce((text: string) => {
      if (isUserInputRef.current) {
        sendGtmLocationEvent(GTM_EVENT_SPACE_ADDRESS_SEARCH, { search_keyword: text })
      }

      searchPlaces(text)
    }, 1000)
  }, [searchPlaces])

  const getGeocode = useCallback(
    async (placeId: string) => {
      const result = await goolgeMapGetPlaceDetail(placeId, cookies.jwt_token)

      if (result.status === 'OK') {
        const {
          result: { geometry },
        } = result
        return geometry.location
      }

      return null
    },
    [cookies.jwt_token]
  )

  const selectPlace = useCallback(
    async (place: PlaceAutocompletePrediction) => {
      const location = await getGeocode(place.place_id)

      if (location) {
        sendGtmLocationEvent(GTM_EVENT_LOCATION_SEARCH_SELECT, {
          ...toAnalyticsLocation(location.lat, location.lng),
          search_keyword: searchText,
          selected_location: place.description,
        })
      }

      return location
    },
    [getGeocode, searchText]
  )

  useEffect(() => {
    if (searchText) {
      debouncedSearch(searchText)
    } else {
      debouncedSearch.cancel()
      setPlaces([])
      setStatus(null)
    }

    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch, searchText])

  return {
    places,
    status,
    searchText,
    setSearchText,
    setSearchTextFromInput,
    setPlaces,
    getGeocode,
    selectPlace,
  }
}
